import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, MapPin, ExternalLink, Loader2, UserPlus, UserMinus, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

export default function CommunityDetail() {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [community, setCommunity] = useState(null);
  const [members, setMembers] = useState([]);
  const [isMember, setIsMember] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actLoading, setActLoading] = useState(false);

  async function loadAll() {
    try {
      const [cData, mData, joinedData] = await Promise.all([
        api(`/communities/${communityId}`),
        api(`/communities/${communityId}/members`).catch(() => ({ members: [] })),
        api('/communities/joined/list').catch(() => ({ communities: [] })),
      ]);
      setCommunity(cData.community);
      setMembers(mData.members || []);
      setIsMember((joinedData.communities || []).some((c) => c.id === communityId));
    } catch (err) {
      toast.error(err.message || 'Failed to load');
      navigate('/app/communities');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  async function handleToggleMembership() {
    if (actLoading) return;
    setActLoading(true);
    try {
      if (isMember) {
        await api(`/communities/${communityId}/leave`, { method: 'DELETE' });
        toast.success('Left community');
      } else {
        await api(`/communities/${communityId}/join`, { method: 'POST' });
        toast.success(`Welcome to ${community.name}!`);
      }
      await loadAll();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setActLoading(false);
    }
  }

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C8553D]" /></div>;
  if (!community) return null;

  const img = community.images?.[0];

  return (
    <div className="max-w-4xl mx-auto pb-12" data-testid={`community-detail-${community.id}`}>
      {/* Hero */}
      <div className="relative aspect-[16/9] sm:aspect-[3/1] bg-[#FFE5DD] mb-6">
        {img ? (
          <img src={img} alt={community.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Users className="w-16 h-16 text-[#C8553D]/30" />
          </div>
        )}
        <button
          onClick={() => navigate('/app/communities')}
          data-testid="community-back-button"
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md"
        >
          <ArrowLeft className="w-5 h-5 text-[#2B2D42]" />
        </button>
      </div>

      <div className="px-4 sm:px-6">
        <p className="text-xs font-bold uppercase tracking-wider text-[#C8553D]" data-testid="community-detail-category">
          {community.category}
        </p>
        <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2B2D42] mt-1" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="community-detail-name">
          {community.name}
        </h1>
        <div className="flex items-center gap-4 text-sm text-[#6D6875] mt-2">
          <span className="flex items-center gap-1.5">
            <MapPin className="w-4 h-4" /> {community.city}
          </span>
          <span className="flex items-center gap-1.5" data-testid="community-detail-member-count">
            <Users className="w-4 h-4" /> {(community.member_count || 0).toLocaleString()} members
          </span>
        </div>

        {/* Join / Leave */}
        <button
          onClick={handleToggleMembership}
          disabled={actLoading}
          data-testid="community-join-button"
          className={`mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-colors disabled:opacity-60 ${
            isMember
              ? 'bg-white border-2 border-[#C8553D] text-[#C8553D] hover:bg-[#FFE5DD]'
              : 'bg-[#C8553D] hover:bg-[#9E3A26] text-white shadow-lg shadow-[#C8553D]/30'
          }`}
        >
          {actLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isMember ? (
            <>
              <UserMinus className="w-4 h-4" /> Leave Community
            </>
          ) : (
            <>
              <UserPlus className="w-4 h-4" /> Join Community
            </>
          )}
        </button>

        {/* Description */}
        <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mt-5 mb-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-2">About</h2>
          <p className="text-[#2B2D42] leading-relaxed whitespace-pre-wrap" data-testid="community-detail-description">{community.description}</p>
        </section>

        {/* Activities */}
        {community.activities?.length > 0 && (
          <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5" data-testid="community-activities">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-3">Activities</h2>
            <div className="flex flex-wrap gap-2">
              {community.activities.map((a) => (
                <span key={a} className="px-3 py-1 bg-[#FFE5DD] text-[#C8553D] rounded-full text-xs font-semibold">
                  {a}
                </span>
              ))}
            </div>
            {community.meeting_frequency && (
              <p className="text-sm text-[#6D6875] mt-3">📆 Meets {community.meeting_frequency}</p>
            )}
          </section>
        )}

        {/* Links */}
        {(community.website_url || community.instagram_url || community.linktree_url) && (
          <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5" data-testid="community-links">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-3">Find them online</h2>
            <div className="flex flex-wrap gap-3">
              {community.website_url && (
                <a href={community.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-[#C8553D] hover:underline">
                  <Globe className="w-4 h-4" /> Website
                </a>
              )}
              {community.instagram_url && (
                <a href={community.instagram_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-[#C8553D] hover:underline">
                  <ExternalLink className="w-4 h-4" /> Instagram
                </a>
              )}
              {community.linktree_url && (
                <a href={community.linktree_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-[#C8553D] hover:underline">
                  <ExternalLink className="w-4 h-4" /> Linktree
                </a>
              )}
            </div>
          </section>
        )}

        {/* Members preview */}
        {members.length > 0 && (
          <section data-testid="community-members">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-3">
              Members ({members.length}{members.length >= 20 ? '+' : ''})
            </h2>
            <div className="flex flex-wrap gap-2">
              {members.slice(0, 12).map((m) => (
                <div key={m.id} className="bg-white border border-[#F4F1DE] rounded-full pl-1 pr-3 py-1 flex items-center gap-2" title={m.name}>
                  <div className="w-7 h-7 rounded-full overflow-hidden bg-[#FFE5DD] flex-shrink-0">
                    {m.photos?.[0] ? (
                      <img src={m.photos[0]} alt={m.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs font-bold text-[#C8553D]">
                        {m.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <span className="text-xs font-semibold text-[#2B2D42]">{m.name?.split(' ')[0]}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
